import React from "react";
import { Trophy, Gift, Landmark, Check, Search, MessageSquare, Send, Mic } from "lucide-react";

const rise = (i = 0) => ({ animation: `azRise 6s ${(i * 0.45).toFixed(2)}s ease-in-out infinite` });

const Frame = ({ title, children }) => (
  <div className="rounded-2xl border border-border bg-card shadow-xl overflow-hidden">
    <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border bg-muted/40">
      <span className="h-2.5 w-2.5 rounded-full bg-red-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
      <span className="ml-3 text-xs text-muted-foreground font-medium">{title}</span>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

export const RewardsShot = () => (
  <Frame title="Client — rewards">
    <div className="flex items-center justify-between mb-3" style={rise(0)}>
      <div><div className="text-[9px] uppercase tracking-wider text-muted-foreground">Points</div><div className="font-heading font-extrabold text-2xl">1,240</div></div>
      <div className="text-right text-xs"><div className="text-muted-foreground">Next reward</div><div className="font-semibold">$25 credit · 260 to go</div></div>
    </div>
    <div className="h-2 rounded-full bg-muted overflow-hidden mb-3" style={rise(1)}><div className="h-full bg-primary origin-left" style={{ width: "83%", animation: "azBar 6s ease-in-out infinite" }} /></div>
    <div className="flex gap-1.5 flex-wrap mb-3">
      {[["Regular", Trophy], ["Referrer ×3", Gift], ["Retail fan", Check]].map(([l, I], i) => <span key={l} style={rise(i + 2)} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-primary/10 text-primary"><I className="h-3 w-3" />{l}</span>)}
    </div>
    <div className="rounded-xl border border-border p-2.5 text-xs flex items-center justify-between" style={rise(5)}>
      <span>Sophie referred <b>Mia</b> · first paid visit done</span><span className="font-semibold text-emerald-600">+200 pts</span>
    </div>
  </Frame>
);

export const PayoutsShot = () => (
  <Frame title="Dashboard — Stripe payouts">
    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
      {[["Arriving Thu", "$2,184.50", "text-emerald-600"], ["Pending", "$960.00", ""]].map(([l, v, c], i) => (
        <div key={l} className="rounded-xl border border-border p-3" style={rise(i)}>
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Landmark className="h-3 w-3" />{l}</div>
          <div className={`font-heading font-extrabold text-xl tabular-nums ${c}`}>{v}</div>
        </div>
      ))}
    </div>
    <div className="rounded-xl border border-border p-2.5 text-xs space-y-1" style={rise(2)}>
      <div className="flex justify-between font-medium"><span>Payout 28 Sep · reconcile</span><span className="text-emerald-600">14 / 14 matched</span></div>
      {[["Gross", "$2,230.00"], ["Stripe fees", "−$38.50"], ["Refunds", "−$7.00"]].map(([l, v]) => <div key={l} className="flex justify-between text-muted-foreground"><span>{l}</span><span className="tabular-nums">{v}</span></div>)}
      <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>Net to bank</span><span className="tabular-nums">$2,184.50</span></div>
    </div>
  </Frame>
);

export const SidePanelShot = () => (
  <Frame title="Diary — coming up">
    <div className="flex items-center gap-2 h-9 rounded-lg border border-border px-2.5 text-xs text-muted-foreground mb-3" style={rise(0)}><Search className="h-3.5 w-3.5" />0411 222 333</div>
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-2.5 text-xs mb-3" style={rise(1)}>
      <div className="font-semibold text-sm">Emma Walker</div>
      <div className="text-muted-foreground">Next: Thu 2:00pm · Full body laser · $25 credit</div>
      <div className="flex gap-1.5 mt-2">{["Open", "Book new", "Text details"].map((b, i) => <span key={b} className={`px-2 py-1 rounded-md text-[10px] font-semibold ${i === 2 ? "bg-primary text-primary-foreground" : "border border-border"}`}>{b}</span>)}</div>
    </div>
    <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1">Thursday</div>
    {[["2:00pm", "Emma · Full body laser"], ["3:30pm", "Amira · Hydrafacial"], ["5:00pm", "Jade · Consult"]].map(([t, l], i) => (
      <div key={t} style={rise(i + 2)} className="flex items-center gap-2 text-xs py-1 border-b border-border/60 last:border-0"><span className="font-mono text-muted-foreground w-12">{t}</span><span>{l}</span></div>
    ))}
    <div className="mt-2 text-[10px] text-muted-foreground bg-muted/50 rounded-lg p-2">“…Your appointments: azordi.app/s/Ab3xYz” — one tap, no login.</div>
  </Frame>
);

export const ConversationsShot = () => (
  <Frame title="Azordi Intelligence — conversations">
    <div className="flex justify-end mb-2" style={rise(0)}>
      <div className="bg-primary text-primary-foreground rounded-2xl px-3 py-1.5 text-[11px] max-w-[80%]">Who should I text today?</div>
    </div>
    <div className="flex justify-start mb-2" style={rise(1)}>
      <div className="bg-muted rounded-2xl px-3 py-1.5 text-[11px] max-w-[88%]">Nine clients are due and Thursday has six empty hours. Emma, Sophie and Amira usually book on a nudge — want me to text them?</div>
    </div>
    <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-2.5 mb-2" style={rise(2)}>
      <div className="text-[9px] font-bold uppercase tracking-widest text-primary mb-1">Confirm action</div>
      <div className="text-[11px] leading-snug">Text <b>3 clients</b> a rebook nudge for Thursday</div>
      <div className="flex gap-1.5 mt-2"><span className="text-[10px] font-semibold px-2.5 py-1 rounded-md bg-primary text-primary-foreground">Yes, do it</span><span className="text-[10px] font-semibold px-2.5 py-1 rounded-md border border-border">Cancel</span></div>
    </div>
    <div className="flex items-center gap-2 h-8 rounded-lg border border-border px-2 text-[11px] text-muted-foreground" style={rise(3)}><MessageSquare className="h-3 w-3" />Ask Azordi… it remembers last week's thread<Send className="h-3 w-3 ml-auto" /></div>
  </Frame>
);

export const BriefShot = () => (
  <Frame title="Azordi Intelligence — today's brief">
    {[["What's happening", "7 in today · Amira at 11 (birthday) · $1,340 booked", "primary"],
      ["Needs doing", "9 clients due — text them · reorder numbing cream", "amber"],
      ["Trajectory", "Rebooking up to 68% · month tracking $52.9k", "green"]].map(([h, b, t], i) => (
      <div key={h} style={rise(i)} className="mb-2 last:mb-0">
        <div className={`text-[9px] uppercase tracking-wider font-semibold mb-0.5 ${t === "amber" ? "text-amber-600" : t === "green" ? "text-emerald-600" : "text-primary"}`}>{h}</div>
        <div className="text-[11px] leading-snug">{b}</div>
      </div>
    ))}
    <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full border border-border" style={rise(3)}><Mic className="h-3 w-3 text-primary" />Read it to me</div>
  </Frame>
);
